import { AfterViewInit, Component, ElementRef, inject, Injector, OnInit, signal, ViewChild, input, output } from '@angular/core'
import { form, required } from '@angular/forms/signals'
import { MatAutocompleteTrigger } from '@angular/material/autocomplete'
import { LaskunYksikko } from '@lemon/core-firebase/model/laskun-yksikot'
import { YksikkoService } from '@lemon/core-firebase/service/lasku/yksikko.service'
import { Observable } from 'rxjs'
import { map, startWith } from 'rxjs/operators'

import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'

interface YksikkoehdotuksetNakymassa {
  naytetaankoYleisimmat: boolean
  yksikot: LaskunYksikko[]
}

@Component({
  selector: 'app-yksikko-haku',
  templateUrl: './yksikko-haku.component.html',
  styleUrls: ['./yksikko-haku.component.css'],
  standalone: false,
})
export class YksikkoHakuComponent implements OnInit, AfterViewInit {
  @ViewChild('hakukentta') hakukentta: ElementRef<HTMLInputElement>
  @ViewChild(MatAutocompleteTrigger) hakulistanTrigger: MatAutocompleteTrigger

  readonly yksikko = input<string>()
  readonly otsikko = input<string>()
  readonly naytaKenttana = input(false)
  readonly yksikkoValittu = output<string>()
  readonly suljettu = output<void>()

  readonly hakusana = form(signal(''), polku => required(polku))
  yksikkoehdotuksetObservable: Observable<YksikkoehdotuksetNakymassa>

  private readonly _injector = inject(Injector)

  constructor(private _yksikkoService: YksikkoService) {}

  ngOnInit() {
    const alkuarvo = this.naytaKenttana() ? (this.yksikko() ?? '') : ''
    asetaArvo(this.hakusana, alkuarvo)
    this.yksikkoehdotuksetObservable = arvonMuutokset(this.hakusana, { injector: this._injector }).pipe(
      startWith(alkuarvo),
      map(hakusana => this._annaYksikkoehdotukset(hakusana)),
    )
  }

  ngAfterViewInit() {
    if (!this.naytaKenttana()) {
      setTimeout(() => this.hakukentta?.nativeElement.focus())
    }
  }

  valitseHakukentanTeksti() {
    this.hakukentta?.nativeElement.select()
  }

  valitseYksikko(tunnus: string) {
    asetaArvo(this.hakusana, tunnus)
    this.yksikkoValittu.emit(tunnus)
  }

  kasitteleFokuksenMenetys() {
    const onkoHakulistaAuki = this.hakulistanTrigger?.panelOpen
    if (!onkoHakulistaAuki) {
      this.vahvistaHaku()
    }
  }

  vahvistaHaku() {
    const tunnistettuYksikko = this._yksikkoService.annaYksikkoTunnuksellaTaiNimella(this.hakusana().value())
    if (tunnistettuYksikko) {
      this.valitseYksikko(tunnistettuYksikko.tunnus)
      return
    }
    if (this.naytaKenttana()) {
      asetaArvo(this.hakusana, this.yksikko() ?? '')
      return
    }
    this.suljettu.emit()
  }

  peruutaHaku() {
    this.suljettu.emit()
  }

  private _annaYksikkoehdotukset(hakusana: string): YksikkoehdotuksetNakymassa {
    const naytetaankoYleisimmat = !hakusana?.trim() || hakusana === this.yksikko()
    return {
      naytetaankoYleisimmat,
      yksikot: this._yksikkoService.haeYksikoita(naytetaankoYleisimmat ? '' : hakusana, 10),
    }
  }
}
